import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ICartDetails } from '../appModels/ICartDetails';
import { CheckoutResolver } from '../pages/checkout/services/checkout.resolver';
import { KioskSettingsService } from '../appServices/kiosk-settings.service';
import { OrderTypeResolver } from './order-type.resolver';
import { CartSessionEnvelopeService } from './cart-session-envelope.service';
import { CheckoutHelper } from '../pages/checkout/services/checkout.helper';

@Injectable({
    providedIn: 'root'
})
export class CartStorage {

    tipAmount = 0;
    cart = new BehaviorSubject<ICartDetails[]>([]);
    coupon = new BehaviorSubject<any>({ couponCode: null, couponValue: 0 });
    couponValue: number = 0;
    couponCode!: String;

    tax: any;
    taxIncluded: boolean = true;

    constructor(
        private checkoutHelper: CheckoutHelper,
        private cartSession: CartSessionEnvelopeService
    ) {
        this.cart.next(this.cartSession.load().items);
        this.getTipAmount();
        this.getCoupon();
        this.subscribeTax();
        this.subscribeTaxIncluded();
    }


    /**
* get Tax
*/
    subscribeTax() {
        this.checkoutHelper.tax.subscribe(res => {
            this.tax = res;
        })
    }

    subscribeTaxIncluded() {
        this.checkoutHelper.taxIncluded.subscribe(res => {
            this.taxIncluded = !!res;
        });
    }
    /**
     * add product in cart
     * @param cartDetail 
     */
    addProductInCart(cartDetail: ICartDetails) {

        const cartCopy = [...this.cart.value, cartDetail];
        this.cart.next(cartCopy);
        this.updateSessionStorage();
    }

    /**
     * remove product from cart
     * @param index 
     */
    removeProductFromCart(index: number) {
        const cartCopy = [...this.cart.value];
        cartCopy.splice(index, 1);
        this.cart.next(cartCopy);
        this.updateSessionStorage();
    }

    /**
     * update product in cart
     * @param index 
     * @param cartDetail 
     */
    updateProductInCart(index: number, cartDetail: ICartDetails) {
        const cartCopy = [...this.cart.value];
        cartCopy[index] = cartDetail;
        this.cart.next(cartCopy);
        this.updateSessionStorage();
    }

    /**
     * Remove cart lines that are no longer present in a server-validated menu.
     *
     * This method must only be called with a complete menu snapshot returned by
     * the API. A malformed value is ignored so a network/parsing failure can
     * never empty a customer's cart.
     *
     * @returns the cart lines removed from the current order
     */
    reconcileWithValidatedMenu(menu: any): ICartDetails[] {
        if (!Array.isArray(menu)) {
            return [];
        }

        const isCompleteSnapshot = menu.every(
            (category: any) =>
                category != null &&
                typeof category === 'object' &&
                category.id != null &&
                Array.isArray(category.products) &&
                category.products.every(
                    (product: any) =>
                        product != null &&
                        typeof product === 'object' &&
                        product.id != null
                )
        );
        if (!isCompleteSnapshot) {
            return [];
        }

        const availableProductIds = new Set<string>();
        menu.forEach((category: any) => {
            if (category?.active === false || !Array.isArray(category?.products)) {
                return;
            }

            category.products.forEach((product: any) => {
                if (product?.active === false || product?.id == null) {
                    return;
                }
                availableProductIds.add(String(product.id));
            });
        });

        const removed: ICartDetails[] = [];
        const retained = this.cart.value.filter((cartLine) => {
            const productId = cartLine?.productModel?.id;
            const isAvailable =
                productId != null && availableProductIds.has(String(productId));
            if (!isAvailable) {
                removed.push(cartLine);
            }
            return isAvailable;
        });

        if (removed.length > 0) {
            this.cart.next(retained);
            // Changing the cart must also invalidate the current idempotency
            // key so a later checkout cannot replay the pre-refresh order.
            this.updateSessionStorage();
        }

        return removed;
    }

    /**
     * get cart details by index
     * @param index 
     */
    getCartDetailsByIndex(index: number) {
        return JSON.parse(JSON.stringify(this.cart.value[index]));
    }

    /**
     * clear cart
     */
    clearCart() {
        this.cart.next([]);
        this.clearSessionStorage();
    }
    /**
     * clear cart without dialog
     */
    public clearCartWithOutDialog() {
        this.cart.next([]);
        this.clearSessionStorage();
    }

    /**
     * update session storage
     *
     * Called on every cart mutation (add/remove/update). Changing the cart
     * changes the logical order, so the idempotency key must be dropped:
     * otherwise, after a committed-but-lost response, adding an item and
     * retrying would return the OLD order and silently lose the new item.
     */
    private updateSessionStorage() {
        // Cart mutation and UUID invalidation cross one atomic session write.
        this.cartSession.write(this.cart.value, null);
    }

    /**
     * clear session storage
     */
    private clearSessionStorage() {
        this.cartSession.clear();
    }


    // ######### helper methods

    /**
     * calculate per item price
     * @param item 
     */
    public calculatePerItemPrice(item: any): void {
        let price = item.productModel.basePrice;
        if (item.productModel.discount) {
            price = item.productModel.discount.discount;
        }
        const variants = Array.isArray(item.cartDetailsVariantModelList)
            ? item.cartDetailsVariantModelList
            : [];
        const addOns = Array.isArray(item.cartDetailsAddOnModelList)
            ? item.cartDetailsAddOnModelList
            : [];

        variants.forEach((variant: any) => {
            price = price + variant.price;
        });
        addOns.forEach((addOn: any) => {
            price = price + addOn.addOnModel.addOnPrice;
        });
        price = price * item.quantity;
        item.price = price;
    }

    /**
     * get total price of cart
     * @param cartDetails 
     */
    public totalPriceOfCart(): number {
        let total = 0;
        if (this.cart.value) {
            this.cart.value.forEach(element => {
                total = total + element.price;
            });
        }
        return total;
    }

    /**
     * calculate total tax
     */
    public calculateTax(): number {
        if (this.taxIncluded) return 0;
        return (this.totalPriceOfCart() + this.tipAmount) * this.tax / 100;
    }

    /**
     * calculate total
     */
    public calculateTotal(): number {
        // return this.calculateTax(tax) + this.totalPriceOfCart() + this.tipAmount - this?.couponValue;
        // TODO - add coupon
        return this.calculateTax() + this.totalPriceOfCart() + this.tipAmount;
    }

    /**
   * get Tip Amount
   */
    getTipAmount(): void {
        this.checkoutHelper.tipAmount.subscribe(res => {
            this.tipAmount = res;
        });
    }

    /**
     * get coupon
     */
    getCoupon(): void {
        this.coupon.subscribe(res => {
            this.couponValue = res.couponValue;
            this.couponCode = res.couponCode;
        });
    }

}
